import { Box, Link as MuiLink, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { xsHeight, mdHeight } from "components/AppBar";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  CredentialKind,
  CredentialsQuery,
  CredentialVerificationStatus,
} from "generated-graphql/graphql";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { credentialKindToLabel, credentialVerificationStatusToLabel } from "util/constants";

import { CREDENTIALS } from "../../Credentials/schema";

export const FACILITY_CREDENTIALS_SECTION_ID = "facility-credentials";
export const FACILITY_CREDENTIALS_SECTION_ICON = "👤";

type Row = CredentialsQuery["credentials"]["items"][number];

export const FacilityCredentialsSection: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        sortable: true,
      },
      {
        field: "username",
        headerName: "Username",
        flex: 1,
        sortable: true,
      },
      {
        field: "kind",
        headerName: "Type",
        flex: 1,
        sortable: true,
        valueGetter(_, row) {
          return credentialKindToLabel(row.kind);
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(CredentialKind),
        enumPresentationFunction: (enumValue: string) =>
          credentialKindToLabel(enumValue as CredentialKind),
      },
      {
        field: "facilities",
        headerName: "Facilities",
        flex: 0.75,
        sortable: false,
        valueGetter(_, row) {
          return row.facilityCount ?? 0;
        },
      },
      {
        field: "portal.jurisdiction",
        headerName: "State",
        sortable: false,
        flex: 0.75,
        valueGetter(_, row) {
          return row.portal?.jurisdiction ?? "";
        },
      },
      {
        field: "verificationStatus",
        headerName: "Verification Status",
        flex: 1,
        valueGetter(_, row) {
          return prettyPrintEnumValue(row.verificationStatus);
        },
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(CredentialVerificationStatus),
        enumPresentationFunction: (enumValue: string) =>
          credentialVerificationStatusToLabel(enumValue as CredentialVerificationStatus),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
      },
    ],
    []
  );

  return (
    <Box
      id={FACILITY_CREDENTIALS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Typography variant="h6" mb={5}>
        {FACILITY_CREDENTIALS_SECTION_ICON} Credentials
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={5}>
        Showing all current facility credentials. To view more details or edit credentials, view{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/credentials`}>
          Credentials
        </MuiLink>{" "}
        in your catalog.
      </Typography>

      <OmnisearchDataGrid
        persistenceKey={`facility-credentials-v2-${facilityId}`}
        columns={columns}
        dataQuery={CREDENTIALS}
        defaultSearch={`tenantId:${tenantId} facilityId:${facilityId}`}
        getItems={(data) => data.credentials.items}
        getCount={(data) => data.credentials.count ?? 0}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        noDataMessage="No credentials have been added to this facility yet."
        hideOmnisearch
        showManageColumns={false}
        disableRowSelectionOnClick
        excludeFilterColumns={[
          "name",
          "username",
          "kind",
          "facilities",
          "portal.jurisdiction",
          "verificationStatus",
          "email",
        ]}
        rowHeight="auto"
        gridSx={{
          "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            { outline: "none" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-row:hover": {
            cursor: "default",
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-row.Mui-hovered": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};
